import { Image } from '@timelyapp/react';
import type React from 'react';
import { useState } from 'react';
import { linkedWorkIconFallback } from '~/api/linkedWorkItems/linkedWorkItems.icons';

type LinkedWorkIconProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onLoad'> & {
  src: string;
  /** The tool the icon belongs to; decides what replaces an icon that fails (see `linkedWorkIconFallback`). */
  toolId: string | null | undefined;
  /** Rendered when the icon fails and its tool has no fallback image. */
  fallbackElement?: React.ReactElement;
};

/**
 * A linked work icon that never renders broken or blank. Some image hosts answer a missing image
 * with a success status and a 1x1 placeholder (gstatic returns a transparent GIF for an unknown
 * id), which the browser decodes as a successful load, so a 1x1 image counts as a failure too: no
 * real icon is one pixel.
 */
export const LinkedWorkIcon = ({ src, toolId, fallbackElement, ...rest }: LinkedWorkIconProps) => {
  const [placeholderSrc, setPlaceholderSrc] = useState<string | null>(null);
  const fallback = linkedWorkIconFallback(toolId) || fallbackElement || false;

  if (placeholderSrc === src) {
    if (typeof fallback !== 'string') return fallback || null;
    return <Image {...rest} src={fallback} fallback={fallbackElement ?? false} />;
  }

  return (
    <Image
      // Image keeps its failed state across src changes, so each src gets a fresh one.
      key={src}
      {...rest}
      src={src}
      fallback={fallback}
      onLoad={event => {
        const img = event.currentTarget;
        if (img.naturalWidth === 1 && img.naturalHeight === 1) setPlaceholderSrc(src);
      }}
    />
  );
};
